import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon, Download as DownloadIcon, UploadFile as UploadIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { employeeApi } from '../../api/employeeApi';
import { BulkUploadEmployeeRow, BulkUploadEmployeesResponse, BulkUploadedEmployeeInfo } from '../../types';
import { RootState } from '../../store/store';

const REQUIRED_HEADERS = ['Full name', 'Contact Number', 'Email', 'Emplyee Code', 'Gender'];

const SAMPLE_ROWS: string[][] = [
 //  ['Rahul Sharma', '9876543210', 'rahul.sharma@acme.com', 'EMP1001', 'Male'],
//   ['Priya Patel', '9876543211', 'priya.patel@acme.com', 'EMP1002', 'Female'],
//   ['Amit Verma', '9876543212', 'amit.verma@acme.com', 'EMP1003', 'Male'],
];

const parseCsvLine = (line: string): string[] => {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
};

const sanitizeValue = (value: string): string => value.replace(/^"|"$/g, '').trim();
const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, { label: 'All', value: -1 }];

const EmployeeBulkUpload: React.FC = () => {
  const navigate = useNavigate();
  const tenantId = useSelector((state: RootState) => state.auth.user?.tenantId ?? '');
  const [rows, setRows] = useState<BulkUploadEmployeeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingCredentials, setSendingCredentials] = useState(false);
  const [result, setResult] = useState<BulkUploadEmployeesResponse | null>(null);
  const [pendingEmployees, setPendingEmployees] = useState<BulkUploadedEmployeeInfo[]>([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [credentialStatusByEmployeeId, setCredentialStatusByEmployeeId] = useState<Record<string, string>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const successfulEmployees = pendingEmployees;
  const allSelected = successfulEmployees.length > 0 && selectedEmployeeIds.length === successfulEmployees.length;
  const partiallySelected = selectedEmployeeIds.length > 0 && !allSelected;
  const paginatedEmployees = rowsPerPage === -1
    ? successfulEmployees
    : successfulEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const loadPendingEmployees = useCallback(async () => {
    try {
      const list = await employeeApi.getPendingCredentialEmployees();
      setPendingEmployees(list);
    } catch {
      toast.error('Failed to load pending credential employees.');
    }
  }, []);

  useEffect(() => {
    const maxPage = rowsPerPage === -1 ? 0 : Math.max(0, Math.ceil(successfulEmployees.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [successfulEmployees.length, page, rowsPerPage]);

  useEffect(() => {
    void loadPendingEmployees();
  }, [loadPendingEmployees]);

  const clientValidationErrors = useMemo(() => {
    const errors: string[] = [];

    rows.forEach((row, index) => {
      const line = index + 2;
      if (!row.fullName) errors.push(`Row ${line}: Full name is required.`);
      if (!row.contactNumber) errors.push(`Row ${line}: Contact Number is required.`);
      if (row.contactNumber && !/^[0-9]{10}$/.test(row.contactNumber)) {
        errors.push(`Row ${line}: Contact Number must be exactly 10 digits.`);
      }
      if (!row.email) errors.push(`Row ${line}: Email is required.`);
      if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
        errors.push(`Row ${line}: Email format is invalid.`);
      }
      if (!row.employeeCode) errors.push(`Row ${line}: Emplyee Code is required.`);
      if (!row.gender) errors.push(`Row ${line}: Gender is required.`);
    });

    return errors;
  }, [rows]);

  const handleDownloadSample = () => {
    const allRows = [REQUIRED_HEADERS, ...SAMPLE_ROWS];
    const csv = allRows
      .map((r) => r.map((value) => `"${value.replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'employee_bulk_upload_sample.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (file: File) => {
    const text = await file.text();
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (lines.length < 2) {
      toast.error('CSV file is empty or has no data rows.');
      setRows([]);
      return;
    }

    const headers = parseCsvLine(lines[0]).map(sanitizeValue);

    const missingHeaders = REQUIRED_HEADERS.filter((header) => !headers.includes(header));
    if (missingHeaders.length > 0) {
      toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
      setRows([]);
      return;
    }

    const indexMap = {
      fullName: headers.indexOf('Full name'),
      contactNumber: headers.indexOf('Contact Number'),
      email: headers.indexOf('Email'),
      employeeCode: headers.indexOf('Emplyee Code'),
      gender: headers.indexOf('Gender'),
    };

    const parsedRows = lines.slice(1).map((line) => {
      const cols = parseCsvLine(line).map(sanitizeValue);
      return {
        fullName: cols[indexMap.fullName] ?? '',
        contactNumber: cols[indexMap.contactNumber] ?? '',
        email: cols[indexMap.email] ?? '',
        employeeCode: cols[indexMap.employeeCode] ?? '',
        gender: cols[indexMap.gender] ?? '',
      } as BulkUploadEmployeeRow;
    });

    setResult(null);
    setSelectedEmployeeIds([]);
    setCredentialStatusByEmployeeId({});
    setRows(parsedRows);
    toast.success(`Loaded ${parsedRows.length} row(s) from CSV.`);
  };

  const onFileChange: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;
    // if (!file.name.toLowerCase().endsWith('.csv')) {
    //   toast.error('Please upload a CSV file.');
    //   return;
    // }

    try {
      await handleFileUpload(file);
    } catch {
      toast.error('Failed to parse CSV file.');
    }
  };

  const handleSubmit = async () => {
    if (!tenantId) {
      toast.error('Tenant is missing. Please login again.');
      return;
    }

    if (rows.length === 0) {
      toast.error('Please upload a CSV file with data.');
      return;
    }

    if (clientValidationErrors.length > 0) {
      toast.error('Please fix validation errors before upload.');
      return;
    }

    setLoading(true);
    try {
      const response = await employeeApi.bulkUploadEmployees(rows, tenantId);
      setResult(response);
      await loadPendingEmployees();
      if (response.failedCount === 0) {
        toast.success(`Successfully uploaded ${response.successCount} employee(s).`);
      } else {
        toast.warn(`Uploaded ${response.successCount} employee(s), ${response.failedCount} failed.`);
      }
      setSelectedEmployeeIds([]);
      setCredentialStatusByEmployeeId({});
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Bulk upload failed.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const toggleEmployeeSelection = (employeeId: string) => {
    setSelectedEmployeeIds((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]
    );
  };

  const toggleSelectAllEmployees = () => {
    if (allSelected) {
      setSelectedEmployeeIds([]);
      return;
    }

    setSelectedEmployeeIds(successfulEmployees.map((employee) => employee.employeeId));
  };

  const handleSendCredentials = async () => {
    if (selectedEmployeeIds.length === 0) {
      toast.error('Please select at least one employee.');
      return;
    }

    setSendingCredentials(true);
    const statusUpdates: Record<string, string> = {};

    try {
      await Promise.all(
        selectedEmployeeIds.map(async (employeeId) => {
          const employee = successfulEmployees.find((item) => item.employeeId === employeeId);
          try {
            const sendResult = await employeeApi.sendLoginInstruction(employeeId, employee?.email || undefined);
            if (sendResult.emailSent) {
              statusUpdates[employeeId] = 'Credentials sent';
            } else {
              statusUpdates[employeeId] = sendResult.errorMessage || 'Failed to send';
            }
          } catch (error: any) {
            if (error?.response?.status === 404) {
              statusUpdates[employeeId] = 'Employee no longer exists';
            } else {
              statusUpdates[employeeId] = error?.response?.data?.message || 'Failed to send';
            }
          }
        })
      );

      setCredentialStatusByEmployeeId((prev) => ({ ...prev, ...statusUpdates }));

      const sentCount = Object.values(statusUpdates).filter((value) => value === 'Credentials sent').length;
      const sentIds = Object.entries(statusUpdates)
        .filter(([, status]) => status === 'Credentials sent' || status === 'Employee no longer exists')
        .map(([id]) => id);

      if (sentIds.length > 0) {
        setSelectedEmployeeIds((prev) => prev.filter((id) => !sentIds.includes(id)));
        await loadPendingEmployees();
      }

      if (sentCount > 0) {
        toast.success(`Credentials sent to ${sentCount} employee(s).`);
      }
      if (sentCount < selectedEmployeeIds.length) {
        toast.warn(`${selectedEmployeeIds.length - sentCount} employee(s) could not receive credentials.`);
      }
    } finally {
      setSendingCredentials(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/employees')}>
            Back
          </Button>
          <Typography variant="h5" fontWeight={700}>Employee Bulk Upload</Typography>
        </Stack>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadSample}>
          Download Sample CSV
        </Button>
      </Stack>

      {/* <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Required Columns</Typography>
        <Typography variant="body2" color="text.secondary">
          Full name, Contact Number, Email, Emplyee Code, Gender
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Gender must match an active master value (for example: Male, Female).
        </Typography>
      </Paper> */}

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button variant="contained" component="label" startIcon={<UploadIcon />}>
            Select CSV File
            <input hidden type="file" accept=".csv" onChange={onFileChange} />
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading || rows.length === 0}>
            {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Upload To Employee Table'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {rows.length > 0 ? `${rows.length} row(s) ready` : 'No file loaded'}
          </Typography>
        </Stack>
      </Paper>

      {clientValidationErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Validation issues in selected CSV:</Typography>
          <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
            {clientValidationErrors.slice(0, 10).map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
          {clientValidationErrors.length > 10 && (
            <Typography variant="caption">And {clientValidationErrors.length - 10} more...</Typography>
          )}
        </Alert>
      )}

      {result && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          {/* <Typography variant="h6" sx={{ mb: 1 }}>Upload Result</Typography>
          <Typography variant="body2">Total Rows: {result.totalRows}</Typography>
          <Typography variant="body2" color="success.main">Success: {result.successCount}</Typography>
          <Typography variant="body2" color={result.failedCount > 0 ? 'error.main' : 'text.primary'}>
            Failed: {result.failedCount}
          </Typography> */}

          {result.errors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Failed Rows</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Employee Code</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.errors.map((error, index) => (
                    <TableRow key={`${error.rowNumber}-${index}`}>
                      <TableCell>{error.rowNumber}</TableCell>
                      <TableCell>{error.employeeCode || '-'}</TableCell>
                      <TableCell>{error.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2, mt: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5 }}>
            <Typography variant="h6">Pending Credential List</Typography>
            <Button
              variant="contained"
              onClick={handleSendCredentials}
              disabled={sendingCredentials || selectedEmployeeIds.length === 0 || successfulEmployees.length === 0}
            >
              {sendingCredentials ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Send Credentials'}
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This list stays available. Employees are removed after successful credential email/password send.
          </Typography>

          {successfulEmployees.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No pending employees for credentials.</Typography>
          ) : (
          <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={partiallySelected}
                    onChange={toggleSelectAllEmployees}
                  />
                </TableCell>
                <TableCell>Full name</TableCell>
                <TableCell>Employee Code</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Contact Number</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Credential Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedEmployees.map((employee: BulkUploadedEmployeeInfo) => (
                <TableRow key={employee.employeeId}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedEmployeeIds.includes(employee.employeeId)}
                      onChange={() => toggleEmployeeSelection(employee.employeeId)}
                    />
                  </TableCell>
                  <TableCell>{employee.fullName}</TableCell>
                  <TableCell>{employee.employeeCode}</TableCell>
                  <TableCell>{employee.email}</TableCell>
                  <TableCell>{employee.contactNumber}</TableCell>
                  <TableCell>{employee.gender}</TableCell>
                  <TableCell>{credentialStatusByEmployeeId[employee.employeeId] || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={successfulEmployees.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
          </>
          )}
        </Paper>
    </Box>
  );
};

export default EmployeeBulkUpload;
